import { ResultOption, RouteType, RoutingProfile, Waypoint } from '@/common/constants/map-display-types';
import { StopType } from '@/common/constants/booking-types';

type Weight = {
  metric: number;
  standard: number;
};

export type Item = {
  actualPalletSpaces: number;
  actualWeight: Weight;
  customerItemIdentifier1: string;
  description: string;
  packaging: string;
  expectedPallets: number;
  expectedTrailerSpace?: Weight;
  expectedQuantity: number;
  freightClass: string;
  innerPackQuantity: number;
  innerPackQuantityUnitOfMeasure: string;
  isHazmat: boolean;
  itemEquipment?: {
    coilRacksCount: number;
    flatBedEquipmentDescription: string;
    loadingTypeDescription: string;
    maxTrailerAge: number;
    requiredChainsCount: number;
    requiredLocksCount: number;
    requiredStrapsCount: number;
    tarpDescription: string;
  };
  itemId: string;
  orderNumber: number;
  outerPackQuantity: number;
  packagingDimensions?: number[];
  plu: string;
  referenceNumbers: ReferenceNumber[];
  sku: string;
  value: number;
};

type ItemDetails = {
  items: Item[];
  orders: number[];
  pallets: number;
  quantity: number;
  value: number;
  weight: Weight;
};

export interface Location {
  address1: string;
  address2?: string;
  city: string;
  countryCode: string;
  latitude: number;
  longitude: number;
  name: string;
  postalCode: string;
  stateCode: string;
}

export type ReferenceNumber = {
  description: string;
  value: string;
};

type StopDates = {
  displayName: string;
  value?: string;
  range?: {
    start: string;
    end: string;
  };
  source?: {
    isExpectedValue: boolean;
  };
};

export interface Coordinates {
  latitude: number;
  longitude: number;
}

export enum ShipmentStatus {
  None = '',
  Ordered = 'Ordered',
  Booked = 'Booked',
  InTransit = 'In Transit',
  Delivered = 'Delivered'
}

export type RouteDetail = {
  stops: MappableStop[];
  status: ShipmentStatus;
  showTime?: boolean;
  showStopRedesign?: boolean;
};

export type RouteMapState = {
  showMap: boolean;
  setShowMap: (value: boolean) => void;
};

export type RouteContextInfo = RouteDetail & RouteMapState;

export interface CalculateRouteRequest {
  waypoints: Waypoint[];
  waypointPriority?: number;
  includeShape?: boolean;
  resultOptions?: ResultOption[];
  customerCode?: string;
  carrierCode?: string;
  routingProfile?: RoutingProfile;
  routeType: RouteType;
}

export interface CalculatedRoute {
  metaData?: {
    provider?: string,
    version?: string
  };
  route: {
    summary: {
      distanceMiles?: number,
      distanceKilometers?: number,
      travelTime?: number
    },
    waypoints?: Waypoint[],
    polylines?: string[]
  };
  routeType: RouteType;
}

export interface CalculateRouteResponse {
  calculatedRoutes: CalculatedRoute[];
}

export interface MappableStop {
  itemDetails: ItemDetails;
  location: Location;
  stopId: string;
  stopNumber: number;
  type: StopType;
  status: string;
  stopDates: StopDates[];
  billToIds: string[];
}

export type Stringable =
  | string
  | number
  | boolean
  | {
  toString(): string;
};
